/**
 * QuarantinedRoute — wraps a route element. When media is quarantined,
 * non-admin users see the calm <MediaComingSoon /> page; admins fall
 * through to the underlying component so internal verification still
 * works (admin bypass per operator doctrine).
 *
 * Doctrine guards:
 *   • If `MEDIA_FEATURE_ENABLED === true` → pass through for ALL roles.
 *   • If user.role === "admin" → pass through but render a subtle banner
 *     warning that the route is quarantined in production.
 *   • Otherwise → render <MediaComingSoon /> with the supplied area label.
 *
 * NEVER mount the underlying component inside an admin-only branch
 * with hidden state; mounting still triggers fetches/polling. To
 * prevent ghost network calls for non-admins, the underlying
 * component is NOT rendered at all on the quarantined path.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { MEDIA_FEATURE_ENABLED } from "../lib/mediaFeatureFlag";
import MediaComingSoon from "./media/MediaComingSoon";

const API = process.env.REACT_APP_BACKEND_URL;

function useCurrentUserRole() {
  const [state, setState] = useState({ loading: true, role: null });
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/api/auth/me`, { withCredentials: true })
      .then((r) => { if (!cancelled) setState({ loading: false, role: r.data?.role || null }); })
      .catch(() => { if (!cancelled) setState({ loading: false, role: null }); });
    return () => { cancelled = true; };
  }, []);
  return state;
}

export default function QuarantinedRoute({ area = "Media systems", title, children }) {
  const { loading, role } = useCurrentUserRole();

  // Flag is open → pass through immediately. Cheapest path.
  if (MEDIA_FEATURE_ENABLED) return children;

  // While the role check is in flight, show the calm holding page
  // rather than the underlying component — prevents flash of broken
  // CTA / fetch leaks.
  if (loading) return <MediaComingSoon area={area} title={title} />;

  const isAdmin = (role || "").toLowerCase() === "admin";
  if (isAdmin) {
    // Admin bypass: render the underlying component. We deliberately
    // do not wrap it with a banner here because banners over arbitrary
    // page chrome are intrusive; admins get the bypass affordance on
    // the holding page when they hit a route directly via deep-link
    // intent — they can navigate via /admin if they need diagnostics.
    return children;
  }

  return <MediaComingSoon area={area} title={title} />;
}
